import { CacheService } from './cache.service';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subscription } from 'rxjs';

export const APP_CONTEXT_MAX_LENGTH = 16_000;

/**
 * One mounted frontend source of context for the Agent. The source owns its
 * state shape and how that state becomes text; the registry does not know
 * about pages, routes, selections, or other domain concepts.
 */
export interface AppContextSession<TState> {
  /** Opaque lifecycle key. A newer registration with the same key covers it. */
  readonly key: string;
  /** Human-readable heading included with this session's context. */
  readonly name: string;
  /** A seeded/replaying stream of the source's current state. */
  readonly state$: Observable<TState>;
  /** Return null or blank text when this state contributes no context. */
  readonly describe: (state: TState) => string | null;
}

interface AppContextRegistration {
  readonly id: number;
  readonly key: string;
  readonly name: string;
}

/**
 * Shared registry for Workspace view mementos and page-to-Agent context.
 * View sources read their existing owner live; observable sources cache their
 * latest description. build() synchronously assembles the next Agent turn.
 */
@Injectable({ providedIn: 'root' })
export class AppContextService implements OnDestroy {
  private nextRegistrationId = 0;
  private readonly registrationsByKey = new Map<string, AppContextRegistration[]>();

  private readonly views = new Map<string, ViewRegistration>();
  private restoreRevision = 0;
  get restorationVersion(): number { return this.restoreRevision; }
  private pendingRestore: Record<string, unknown> = {};
  private readonly resetSubscription;

  constructor(private cache: CacheService) {
    this.resetSubscription = cache.sessionChanged$.subscribe(() => {
      this.views.clear();
      this.clearRestore();
    });
  }

  get sessionChanged$(): Observable<void> { return this.cache.sessionChanged$; }

  ngOnDestroy(): void { this.resetSubscription.unsubscribe(); }

  /** Sources retain ownership. Only allowlisted view state belongs here, never
   * forms, credentials, fetched records or business undo operations. */
  registerView<T>(source: ViewContextSource<T>): Subscription {
    const entry: ViewRegistration = {
      name: source.name,
      read: source.read,
      describe: value => source.describe(value as T),
      restore: source.restore ? value => source.restore!(value as T) : undefined,
      active: source.active ?? (() => true),
      priority: source.priority ?? 0,
    };
    // Separate tables on one route must not cover each other's state.
    let key = source.key;
    let ordinal = 1;
    while (this.views.has(key)) key = `${source.key}#${++ordinal}`;
    this.views.set(key, entry);
    this.cache.set('app-context-live', key, entry);
    if (Object.prototype.hasOwnProperty.call(this.pendingRestore, key)) {
      const version = this.restoreRevision;
      // A late child is registering during Angular creation. Restore after
      // that check so its parent inputs are not changed halfway through it.
      queueMicrotask(() => {
        if (version === this.restoreRevision && this.views.get(key) === entry) this.restoreView(key, entry);
      });
    }
    return new Subscription(() => {
      if (this.views.get(key) === entry) {
        this.views.delete(key);
        this.cache.delete('app-context-live', key);
      }
    });
  }

  capture(): ViewContextSnapshot {
    const snapshot: Record<string, unknown> = {};
    for (const [key, source] of this.views) {
      if (!source.restore || this.cache.get('app-context-live', key) !== source) continue;
      try { snapshot[key] = structuredClone(source.read()); } catch { /* isolate invalid sources */ }
    }
    return snapshot;
  }

  /** Navigation calls this only after a successful route activation. Sources
   * mounted later (e.g. after catalog loading) consume their own saved slice. */
  restore(snapshot: ViewContextSnapshot): void {
    ++this.restoreRevision;
    this.pendingRestore = structuredClone(snapshot);
    for (const [key, source] of [...this.views].sort((a, b) => a[1].priority - b[1].priority)) {
      this.restoreView(key, source);
    }
  }

  clearRestore(): void { ++this.restoreRevision; this.pendingRestore = {}; }

  private restoreView(key: string, source: ViewRegistration): void {
    if (!source.restore || !Object.prototype.hasOwnProperty.call(this.pendingRestore, key)) return;
    const value = this.pendingRestore[key];
    delete this.pendingRestore[key];
    try { source.restore(structuredClone(value)); } catch { /* the mounted page remains usable */ }
  }

  register<TState>(session: AppContextSession<TState>): Subscription {
    const registration: AppContextRegistration = {
      id: ++this.nextRegistrationId,
      key: session.key,
      name: session.name,
    };

    const stack = this.registrationsByKey.get(registration.key);
    if (stack) {
      stack.push(registration);
    } else {
      this.registrationsByKey.set(
        registration.key,
        [registration],
      );
    }

    const lifecycle = new Subscription(() => this.remove(registration));
    try {
      const source = this.cache.track(() => session.state$).subscribe({
        next: state => {
          try {
            const description = session.describe(state);
            this.cache.set('agent-context', String(registration.id), description?.trim() || null);
          } catch {
            // A broken description must not retain stale context or stop other
            // sessions. A later emission can still recover this registration.
            this.cache.delete('agent-context', String(registration.id));
          }
        },
        error: () => {
          // An errored source cannot provide current state. Keep its lifecycle
          // isolated so every other registered source remains available.
          this.cache.delete('agent-context', String(registration.id));
        },
      });
      lifecycle.add(source);
    } catch {
      // Custom Observable implementations may throw while being subscribed.
      // Treat that source as unavailable without leaking its registration.
      this.cache.delete('agent-context', String(registration.id));
    }

    return lifecycle;
  }

  build(): string {
    const included: string[] = [];
    let includedLength = 0;
    let omittedCount = 0;

    const sections: { name: string; description: string }[] = [];
    for (const [key, source] of [...this.views].sort((a, b) => a[1].priority - b[1].priority)) {
      try {
        if (this.cache.get('app-context-live', key) !== source || !source.active()) continue;
        const description = source.describe(source.read())?.trim();
        if (description) sections.push({ name: source.name, description });
      } catch { /* one broken page must not hide the current Workspace */ }
    }
    for (const stack of this.registrationsByKey.values()) {
      const registration = stack[stack.length - 1];
      const description = registration && this.cache.get<string>('agent-context', String(registration.id));
      if (!description) continue;

      sections.push({ name: registration.name, description });
    }
    for (const { name, description } of sections) {
      const section = `## ${name.trim()}\n${description}`;
      const separatorLength = included.length ? 2 : 0;
      if (includedLength + separatorLength + section.length > APP_CONTEXT_MAX_LENGTH) {
        omittedCount++;
        continue;
      }

      included.push(section);
      includedLength += separatorLength + section.length;
    }

    let context = included.join('\n\n');
    if (omittedCount) {
      const notice = `[${omittedCount} Agent context session${omittedCount === 1 ? '' : 's'} omitted because the context limit was reached.]`;
      const separator = context ? '\n\n' : '';
      if (context.length + separator.length + notice.length <= APP_CONTEXT_MAX_LENGTH) {
        context += separator + notice;
      }
    }

    return context;
  }

  private remove(registration: AppContextRegistration): void {
    this.cache.delete('agent-context', String(registration.id));
    const stack = this.registrationsByKey.get(registration.key);
    if (!stack) return;

    const index = stack.findIndex(item => item.id === registration.id);
    if (index < 0) return;

    stack.splice(index, 1);
    if (!stack.length) this.registrationsByKey.delete(registration.key);
  }
}

/** Safe navigation mementos, scoped to this authenticated browser visit. */
export type ViewContextSnapshot = Readonly<Record<string, unknown>>;

export interface ViewContextSource<T> {
  readonly key: string;
  readonly name: string;
  readonly read: () => T;
  readonly describe: (state: T) => string | null;
  readonly restore?: (state: T) => void;
  /** Hidden master panes may be captured, but are not the Agent's active view. */
  readonly active?: () => boolean;
  /** Page filters restore before child query controls. */
  readonly priority?: number;
}

interface ViewRegistration {
  name: string;
  read: () => unknown;
  describe: (state: unknown) => string | null;
  restore?: (state: unknown) => void;
  active: () => boolean;
  priority: number;
}
